import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import type {
  AddMembershipInput,
  CreateSubjectViewInput,
} from '../../domain/entities/content.entity';

export function useSubjectViews() {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: ['content', 'views'],
    queryFn: () => repository.listSubjectViews(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useSubjectView(viewId: string | undefined) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: ['content', 'view', viewId],
    queryFn: () => repository.getSubjectView(viewId!),
    enabled: !!viewId,
    retry: false,
  });
}

export function useCreateSubjectView() {
  const repository = container.resolve(ContentRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSubjectViewInput) =>
      repository.createSubjectView(input),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['content', 'views'] }),
  });
}

export function useAddMembership(viewId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddMembershipInput) =>
      repository.addMembership(viewId, input),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ['content', 'view', viewId],
      }),
  });
}

export function useRemoveMembership(viewId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subjectId: string) =>
      repository.removeMembership(viewId, subjectId),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ['content', 'view', viewId],
      }),
  });
}
